/**
 * OUTGOING MESSAGE LIST — la coda delle mail in uscita della Smart Inbox, paginata e ordinata
 * server-side.
 *
 * È la pagina «Sorveglianza» (FILO-58, rotta `/surveillance`): si apre dalla sua voce di menu e dal
 * link della Smart Inbox, e si spegne per company come le altre voci del core. Mostra le mail che
 * il CRM ha spedito, o che con l'invio differito aspettano di partire: l'oggetto apre il dettaglio,
 * dove stanno Invia, Modifica e Annulla. Pattern di riferimento: `KanbanList.tsx`, con lo stato in
 * locale invece che in uno slice, perché la coda non serve ad altre schermate.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import { type GridColDef } from '@mui/x-data-grid';
import { formatDateTime, useServerListParams, useServerSearch } from '@crm/shared';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { ServerDataGrid } from '../../components/ServerDataGrid';
import { SearchGlyph } from '../../components/icons';
import { extractError } from '../../store/thunkHelpers';
import { OutgoingMessageDialog } from './OutgoingMessageDialog';
import { outgoingMessageApi } from './outgoingMessageApi';
import {
  OUTGOING_MESSAGE_STATUSES,
  type OutgoingMessage,
  type OutgoingMessageStatus,
} from './outgoingMessageTypes';

export default function OutgoingMessageList() {
  const { t } = useTranslation();
  const groupId = useCurrentGroupId();
  const [items, setItems] = useState<OutgoingMessage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<OutgoingMessageStatus | ''>('');
  const [selected, setSelected] = useState<OutgoingMessage | null>(null);

  const { search, setSearch, query } = useServerSearch();
  const {
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    resetPage,
    backendParams,
  } = useServerListParams({ initialPageSize: 25 });

  useEffect(() => { resetPage(); }, [query, status, resetPage]);

  const load = useCallback(() => {
    setLoading(true);
    outgoingMessageApi
      .getAll(groupId, { ...backendParams, search: query || undefined, status: status || undefined })
      .then((page) => {
        setItems(page.items);
        setTotalCount(page.totalCount);
        setError(null);
      })
      .catch((err) => setError(extractError(err)))
      .finally(() => setLoading(false));
  }, [groupId, backendParams, query, status]);

  useEffect(() => { load(); }, [load]);

  // Dopo un'azione la riga si sostituisce sul posto: con un filtro per stato il ricaricamento la
  // farebbe sparire sotto gli occhi di chi l'ha appena inviata.
  const handleChanged = useCallback((updated: OutgoingMessage) => {
    setItems((prev) => prev.map((m) => (m.outgoingMessageId === updated.outgoingMessageId ? updated : m)));
    setSelected(updated);
  }, []);

  const columns = useMemo(() => outgoingMessageColumns(t, setSelected), [t]);

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('nav.surveillance')}</h1>
          <span className="page-sub">{t('common.record', { count: totalCount })}</span>
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('outgoingMessage.searchPlaceholder')}
              aria-label={t('outgoingMessage.searchPlaceholder')}
            />
          </div>
        </div>
      </div>

      {/* Il filtro per stato è una scelta su cosa guardare: sta fuori dalla card, come i filtri
          delle pratiche di Pollini. */}
      <div className="list-tabs list-tabs--out" role="group" aria-label={t('outgoingMessage.statusFilter')}>
        <button type="button" className={status === '' ? 'on' : undefined} aria-pressed={status === ''}
          onClick={() => setStatus('')}>
          {t('outgoingMessage.allStatuses')}
        </button>
        {OUTGOING_MESSAGE_STATUSES.map((s) => (
          <button key={s.value} type="button" className={status === s.value ? 'on' : undefined}
            aria-pressed={status === s.value} onClick={() => setStatus(s.value)}>
            {t(s.label)}
          </button>
        ))}
      </div>

      <div className="list-card">
        {error && <p className="text-error list-feedback">{t('common.error', { message: error })}</p>}
        {!error && !loading && items.length === 0 && (
          <p className="text-muted list-feedback">{t('outgoingMessage.empty')}</p>
        )}
        {items.length > 0 && (
          <ServerDataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row.outgoingMessageId}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            loading={loading}
            getRowHeight={() => 64}
            className="tk-grid"
          />
        )}
      </div>

      {selected && (
        <OutgoingMessageDialog
          key={selected.outgoingMessageId}
          outgoingMessage={selected}
          onClose={() => setSelected(null)}
          onChanged={handleChanged}
        />
      )}
    </div>
  );
}

function outgoingMessageColumns(
  t: (key: string, opts?: Record<string, unknown>) => string,
  open: (outgoingMessage: OutgoingMessage) => void,
): GridColDef<OutgoingMessage>[] {
  return [
    {
      field: 'createDate', headerName: t('outgoingMessage.columns.createDate'), width: 170,
      valueFormatter: (value: string) => formatDateTime(value),
    },
    {
      // Lookup sul DTO: il backend non ordina su un campo derivato.
      field: 'mailboxEmailAddress', headerName: t('outgoingMessage.columns.mailbox'), width: 220, sortable: false,
      valueGetter: (_value: unknown, row: OutgoingMessage) => row.mailboxEmailAddress ?? t('outgoingMessage.portal'),
    },
    {
      field: 'to', headerName: t('outgoingMessage.columns.to'), width: 220, sortable: false,
      valueGetter: (_value: unknown, row: OutgoingMessage) => [...row.to, ...row.cc, ...row.bcc].join(', '),
    },
    {
      field: 'subject', headerName: t('outgoingMessage.columns.subject'), flex: 1,
      renderCell: (params) => (
        <span className="lc-name">
          <button type="button" className="nm" onClick={() => open(params.row)}>
            {params.row.subject || t('outgoingMessage.noSubject')}
          </button>
        </span>
      ),
    },
    {
      field: 'status', headerName: t('outgoingMessage.columns.status'), width: 130,
      renderCell: (params) => {
        const s = OUTGOING_MESSAGE_STATUSES.find((x) => x.value === params.row.status);
        return s ? <Chip size="small" color={s.color} label={t(s.label)} /> : null;
      },
    },
    {
      field: 'statusDate', headerName: t('outgoingMessage.columns.statusDate'), width: 170,
      valueFormatter: (value: string | null) => (value ? formatDateTime(value) : ''),
    },
  ];
}
